import { PlatformComponentState } from '@models/platformStatus.model';

type BadgeVariant = 'success' | 'warning' | 'error' | 'secondary' | 'primary';

interface StatePresentation {
  /** Passed to StatusIndicator, which takes a raw hex rather than a class. */
  color: string;
  badgeVariant: BadgeVariant;
  label: string;
}

/**
 * Semantic colours from the MUI theme (`src/theme.ts`).
 *
 * Tailwind's config here carries only project-state colours and no generic
 * success/warning/danger tokens, so these are the closest thing the app has to a
 * single source of truth for "good, worrying, bad".
 */
const FEEDBACK_SUCCESS = '#00A76D';
const FEEDBACK_WARNING = '#F5AB23';
const FEEDBACK_ERROR = '#DE0A01';
const NEUTRAL = '#657182';
const INFO = '#215282';

/**
 * One place that decides how a state looks.
 *
 * Two surfaces render these — each component row and the admin screen's own
 * summaries — and without a shared map the same `switch` gets copied and drifts.
 * The copy that falls behind is the one that renders an outage in green.
 */
export const STATE_PRESENTATION: Record<
  PlatformComponentState,
  StatePresentation
> = {
  [PlatformComponentState.OPERATIONAL]: {
    color: FEEDBACK_SUCCESS,
    badgeVariant: 'success',
    label: 'Operational',
  },
  [PlatformComponentState.DEGRADED]: {
    color: FEEDBACK_WARNING,
    badgeVariant: 'warning',
    label: 'Degraded',
  },
  [PlatformComponentState.OUTAGE]: {
    color: FEEDBACK_ERROR,
    badgeVariant: 'error',
    label: 'Outage',
  },
  [PlatformComponentState.MAINTENANCE]: {
    color: INFO,
    badgeVariant: 'primary',
    label: 'Maintenance',
  },
  [PlatformComponentState.UNKNOWN]: {
    color: NEUTRAL,
    badgeVariant: 'secondary',
    label: 'Unknown',
  },
};

/** "2 minutes ago" — the page is only as trustworthy as its timestamp. */
export const formatRelativeTime = (isoDate: string): string => {
  const seconds = Math.round((Date.now() - new Date(isoDate).getTime()) / 1000);

  if (!Number.isFinite(seconds)) return 'unknown';
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};
